import { I, SectionHeading, slugify } from "./shared";

export default function BenefitsSection({ variant, content, accent }) {
  const benefits = content.benefits || [];
  if (!benefits.length) return null;

  if (variant === "bento") {
    return (
      <section className="border-t border-line/70 relative" data-testid="benefits-section-bento">
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <SectionHeading eyebrow="Benefits" title="Why this matters." />
          <div className="grid md:grid-cols-4 gap-5 mt-14">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                data-testid={`benefit-${slugify(b.title)}`}
                className={`group relative rounded-2xl border border-line bg-[var(--gx-surface)]/60 backdrop-blur-md p-7 hover:border-gold/40 transition-all duration-300 ${
                  i === 0 ? "md:col-span-2 flex flex-col justify-end min-h-[220px]" : ""
                }`}
              >
                <div className={`w-11 h-11 rounded-xl border border-line flex items-center justify-center ${accent.icon} mb-5`}>
                  <I name={b.icon} className="w-5 h-5" />
                </div>
                <h3 className={`font-display tracking-tight ${i === 0 ? "text-2xl" : "text-xl"}`}>{b.title}</h3>
                <p className="text-dim text-[13.5px] mt-3 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (variant === "spec-strip") {
    return (
      <section className="border-t border-line/70 bg-[var(--gx-black)]" data-testid="benefits-section-spec-strip">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <SectionHeading eyebrow="Benefits" title="Why this matters." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 divide-line/50 border-y border-line/50 mt-14">
            {benefits.map((b, i) => (
              <div key={b.title} data-testid={`benefit-${slugify(b.title)}`} className="py-8 sm:px-6 sm:border-l border-line/50 first:border-l-0">
                <div className="text-[11px] tracking-[0.3em] uppercase text-mute mb-3">0{i + 1}</div>
                <div className={`w-9 h-9 rounded-lg border border-line flex items-center justify-center ${accent.icon} mb-4`}>
                  <I name={b.icon} className="w-4 h-4" />
                </div>
                <h3 className="font-display text-lg tracking-tight">{b.title}</h3>
                <p className="text-dim text-[13px] mt-3 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (variant === "icon-list") {
    return (
      <section className="border-t border-line/70" data-testid="benefits-section-icon-list">
        <div className="max-w-[980px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <SectionHeading eyebrow="Benefits" title="Why this matters." />
          <div className="mt-14 divide-y divide-line/60 border-y border-line/60">
            {benefits.map((b) => (
              <div key={b.title} data-testid={`benefit-${slugify(b.title)}`} className="flex items-start gap-6 py-7">
                <div className={`w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center ${accent.icon} shrink-0`}>
                  <I name={b.icon} className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-display text-lg tracking-tight">{b.title}</h3>
                  <p className="text-dim text-[13.5px] mt-2 leading-relaxed max-w-[62ch]">{b.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="border-t border-line/70 relative" data-testid="benefits-section-cards">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_70%_-10%,rgba(212,175,55,0.06),transparent_50%)]" />
      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <SectionHeading eyebrow="Benefits" title="Why this matters." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {benefits.map((b) => (
            <div
              key={b.title}
              data-testid={`benefit-${slugify(b.title)}`}
              className="group relative rounded-2xl border border-line bg-[var(--gx-surface)]/60 backdrop-blur-md p-7 hover:border-gold/40 transition-all hover:-translate-y-1 duration-300"
            >
              <div className={`w-11 h-11 rounded-xl border border-line flex items-center justify-center ${accent.icon} mb-5 group-hover:border-gold/40 transition-colors`}>
                <I name={b.icon} className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl tracking-tight">{b.title}</h3>
              <p className="text-dim text-[13.5px] mt-3 leading-relaxed">{b.body}</p>
              <div className="absolute inset-x-7 bottom-7 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
